import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './FocusScreen.css';

const TOTAL_SECONDS = 25 * 60;

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export function FocusScreen() {
  const navigate = useNavigate();
  const [remaining, setRemaining] = useState(TOTAL_SECONDS);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const elapsedBeforePauseRef = useRef(0);

  const progress = 1 - remaining / TOTAL_SECONDS;
  const circumference = 2 * Math.PI * 120;
  const dashOffset = circumference * (1 - progress);

  const start = useCallback(() => {
    setRunning(true);
    startTimeRef.current = Date.now();
    intervalRef.current = window.setInterval(() => {
      const elapsed = elapsedBeforePauseRef.current + Math.floor((Date.now() - startTimeRef.current) / 1000);
      const left = Math.max(0, TOTAL_SECONDS - elapsed);
      setRemaining(left);
      if (left <= 0) {
        clearInterval(intervalRef.current!);
        setRunning(false);
        setFinished(true);
        saveSession();
      }
    }, 200);
  }, []);

  const pause = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    elapsedBeforePauseRef.current += Math.floor((Date.now() - startTimeRef.current) / 1000);
    setRunning(false);
  }, []);

  const stop = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setRunning(false);
    setRemaining(TOTAL_SECONDS);
    elapsedBeforePauseRef.current = 0;
  }, []);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const saveSession = () => {
    try {
      const raw = localStorage.getItem('focusup_stats');
      const stats = raw ? JSON.parse(raw) : { totalSessions: 0, totalMinutes: 0, currentStreak: 0, bestStreak: 0, todayMinutes: 0, todaySessions: 0, lastSessionDate: null };
      const today = new Date().toISOString().slice(0, 10);
      const isNewDay = stats.lastSessionDate !== today;

      stats.totalSessions += 1;
      stats.totalMinutes += 25;
      if (isNewDay) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        if (stats.lastSessionDate === yesterday.toISOString().slice(0, 10)) {
          stats.currentStreak += 1;
        } else {
          stats.currentStreak = 1;
        }
        stats.todayMinutes = 25;
        stats.todaySessions = 1;
      } else {
        stats.todayMinutes += 25;
        stats.todaySessions += 1;
      }
      stats.bestStreak = Math.max(stats.bestStreak, stats.currentStreak);
      stats.lastSessionDate = today;
      localStorage.setItem('focusup_stats', JSON.stringify(stats));
    } catch {}
  };

  useEffect(() => {
    if (finished) navigate('/reward');
  }, [finished, navigate]);

  return (
    <div className="fu-focus">
      <button className="fu-back" onClick={stop}>✕</button>

      <div className="fu-timer-ring">
        <svg viewBox="0 0 260 260" className="fu-ring-svg">
          <circle cx="130" cy="130" r="120" fill="none" stroke="var(--color-border)" strokeWidth="6" />
          <circle cx="130" cy="130" r="120" fill="none"
            stroke="url(#grad)" strokeWidth="6" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            transform="rotate(-90 130 130)"
            style={{ transition: 'stroke-dashoffset 0.3s' }} />
          <defs>
            <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--color-primary)" />
              <stop offset="100%" stopColor="var(--color-primary-light)" />
            </linearGradient>
          </defs>
        </svg>
        <div className="fu-timer-text">
          <span className="fu-time">{formatTime(remaining)}</span>
          <span className="fu-time-label">{running ? 'Reste concentre' : 'Pret ?'}</span>
        </div>
      </div>

      <div className="fu-controls">
        {!running ? (
          <button className="fu-play-btn" onClick={start}>
            {remaining < TOTAL_SECONDS ? '▶ Reprendre' : '▶ Commencer'}
          </button>
        ) : (
          <button className="fu-pause-btn" onClick={pause}>⏸ Pause</button>
        )}
        {remaining < TOTAL_SECONDS && !running && (
          <button className="fu-stop-btn" onClick={stop}>Abandonner</button>
        )}
      </div>
    </div>
  );
}
